import { useEffect, useRef, useState } from "react";
import { apiGet, apiPatch } from "../api";
import { applyTheme, getInitialTheme, type ThemeName } from "../theme";
import { UI_CONFIG, type DateFormat, type OpenMode } from "../../config/ui";

/**
 * Appearance preferences architecture.
 *
 * Two categories with different sync semantics:
 *
 * Device-level (theme, openMode, timezone):
 * - localStorage is the source of truth on this device
 * - Server-side storage is a fallback used only when localStorage is
 *   empty (new device, cleared cache)
 * - Setting changes write to localStorage immediately and push to
 *   server debounced (1000ms)
 * - Result: each device can have its own settings; new devices inherit
 *   the most recent server-saved values as a starting point
 *
 * Account-level (dateFormat):
 * - Server is the source of truth, syncs across all devices
 * - localStorage is NOT consulted for these
 * - Setting changes push to server immediately
 * - Result: changing date format on one device updates it everywhere
 *
 * On server write failure: logged, not retried, not surfaced to user.
 * Next change overwrites whatever the server has.
 */

const DEFAULTS = {
  theme: "light" as ThemeName,
  openMode: "in-list" as OpenMode,
  timezone: "auto",
  dateFormat: "auto" as DateFormat
};

const THEME_VALUES: readonly ThemeName[] = ["light", "slate", "nord"];
const OPEN_MODE_VALUES: readonly OpenMode[] = ["in-list", "page", "window"];
const DATE_FORMAT_VALUES: readonly DateFormat[] = ["auto", "iso", "long", "us", "eu"];

type ServerPrefsResponse = {
  ok: boolean;
  theme?: string;
  openMode?: string;
  timezone?: string;
  dateFormat?: string;
};

function readOpenModeFromStorage(): OpenMode {
  const raw = localStorage.getItem(UI_CONFIG.storageKeys.openMode);
  if (raw === "in-list" || raw === "page" || raw === "window") return raw;
  return DEFAULTS.openMode;
}

function readTimezoneFromStorage(): string {
  return localStorage.getItem(UI_CONFIG.storageKeys.timezone) || DEFAULTS.timezone;
}

export function useAppearancePrefs(dataMode: "mock" | "api") {
  const [theme, setThemeState] = useState<ThemeName>(() => getInitialTheme());
  const [openMode, setOpenModeState] = useState<OpenMode>(() => readOpenModeFromStorage());
  const [timezone, setTimezoneState] = useState<string>(() => readTimezoneFromStorage());
  const [dateFormat, setDateFormatState] = useState<DateFormat>(DEFAULTS.dateFormat);

  const pendingRef = useRef<Record<string, string>>({});
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Apply initial theme to DOM on first render
  useEffect(() => {
    applyTheme(theme, { persist: false });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch server prefs on mount / after login (when dataMode switches to "api")
  useEffect(() => {
    if (dataMode !== "api") return;
    let alive = true;

    (async () => {
      const res = await apiGet<ServerPrefsResponse>("/v1/preferences");
      if (!alive || !res.ok) return;
      const data = res.data;

      // Device-level: only apply server value when localStorage is empty (new/cleared device)
      if (!localStorage.getItem(UI_CONFIG.storageKeys.theme) && data.theme) {
        if (THEME_VALUES.includes(data.theme as ThemeName)) {
          const t = data.theme as ThemeName;
          setThemeState(t);
          applyTheme(t, { persist: false });
        }
      }
      if (!localStorage.getItem(UI_CONFIG.storageKeys.openMode) && data.openMode) {
        if (OPEN_MODE_VALUES.includes(data.openMode as OpenMode)) {
          const m = data.openMode as OpenMode;
          setOpenModeState(m);
          localStorage.setItem(UI_CONFIG.storageKeys.openMode, m);
        }
      }
      if (!localStorage.getItem(UI_CONFIG.storageKeys.timezone) && data.timezone) {
        setTimezoneState(data.timezone);
        localStorage.setItem(UI_CONFIG.storageKeys.timezone, data.timezone);
      }

      // Account-level: always use server value
      if (data.dateFormat && DATE_FORMAT_VALUES.includes(data.dateFormat as DateFormat)) {
        setDateFormatState(data.dateFormat as DateFormat);
      }
    })();

    return () => {
      alive = false;
    };
  }, [dataMode]);

  // Accumulates pending changes and flushes to server after 1s of inactivity.
  // Rapid changes across different fields all reach the server in a single PATCH.
  function debounceServerSync(payload: Record<string, string>) {
    pendingRef.current = { ...pendingRef.current, ...payload };
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const batch = pendingRef.current;
      pendingRef.current = {};
      apiPatch("/v1/preferences", batch).catch(() => {
        console.error("[useAppearancePrefs] Server sync failed for payload:", batch);
      });
    }, 1000);
  }

  function setTheme(v: ThemeName) {
    setThemeState(v);
    applyTheme(v, { persist: true });
    if (dataMode === "api") debounceServerSync({ theme: v });
  }

  function setOpenMode(v: OpenMode) {
    setOpenModeState(v);
    localStorage.setItem(UI_CONFIG.storageKeys.openMode, v);
    if (dataMode === "api") debounceServerSync({ openMode: v });
  }

  function setTimezone(v: string) {
    setTimezoneState(v);
    localStorage.setItem(UI_CONFIG.storageKeys.timezone, v);
    if (dataMode === "api") debounceServerSync({ timezone: v });
  }

  function setDateFormat(v: DateFormat) {
    setDateFormatState(v);
    // Account-level: immediate server push, no localStorage
    if (dataMode === "api") {
      apiPatch("/v1/preferences", { dateFormat: v }).catch(() => {
        console.error("[useAppearancePrefs] Server sync failed for dateFormat:", v);
      });
    }
  }

  return { theme, openMode, timezone, dateFormat, setTheme, setOpenMode, setTimezone, setDateFormat };
}
